import React, {useEffect, useMemo, useState} from 'react';
import {Chip, Container, IconButton, ListItemIcon, Paper, TextField, InputAdornment, Typography} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import Grid from "@mui/material/Grid2";
import {connect} from "react-redux";
import Loader from "./layout/loading/loader";
import {Link, useParams} from "react-router-dom";
import {patrimoinesActions} from "../../modules/patrimoines/patrimoines.actions";
import PatrimoineMap from "./patrimoine/PatrimoineMap";
import PatrimoineDetailItem from "./patrimoine/PatrimoineDetailItem";
import {FolderDelete} from "@mui/icons-material";
import PageTitle from "./layout/PageTitle";
import Stack from "@mui/material/Stack";
import ButtonCreate from "./layout/ButtonCreate";
import {isAdmin, isExploitant} from "../../utils/roles";
import {helper} from "../../utils/helper";
import Button from "@mui/material/Button";
import MapIcon from '@mui/icons-material/Map';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import PatrimoineDetailList from "./patrimoine/PatrimoineDetailList";
import PatrimoineDetailListV2 from "./patrimoine/PatrimoineDetailListV2";
import Constants from "../../utils/constants";
import {conteneursActions} from "../../modules/conteneurs/conteneurs.actions.js";


const getMode = () => {
    const storedMode = window.localStorage.getItem("patrimoine_view_mode");
    return storedMode || Constants.MODE_LIST;
};

function PatrimoineDetailPage({dispatch, ...props}) {
    const params = useParams();
    const [patrimoineId, setPatrimoineId] = useState(null);
    const [currentConteneurs, setCurrentConteneurs] = useState(null)
    const [mode, setMode] = useState(getMode())
    const [searchTerm, setSearchTerm] = useState("")


    useEffect(() => {
        if (params.hasOwnProperty("id")) {
            setPatrimoineId(params.id);
        }

        return () => {
            setPatrimoineId(null);
        };
    }, [params]);

    useEffect(() => {
        if (patrimoineId && (props.patrimoine?.id !== parseInt(patrimoineId)) && !props.loading && !props.error) {
            dispatch(patrimoinesActions.fetchPatrimoine(patrimoineId));
        }
    }, [dispatch, patrimoineId, props.patrimoine, props.loading, props.error]);

    const handleModeChange = (value) => {
        window.localStorage.setItem("patrimoine_view_mode", value);
        setMode(value)
    }

    const onConteneurUpdate = (conteneur) => {
        let values = {
            latitude: JSON.stringify(conteneur.latitude),
            longitude: JSON.stringify(conteneur.longitude)
        }
        dispatch(conteneursActions.updateConteneur(conteneur.id, values));
    }

    const filteredConteneurs = useMemo(() => {
        const conteneurs = props.patrimoine?.conteneurs || [];
        const term = searchTerm.trim().toLowerCase();
        if (!term) return conteneurs;
        return conteneurs.filter((c) => {
            const fields = [
                c.nomenclature,
                c.adresse,
                c.commune,
                c.conteneurType,
                c.dechetType,
                c.ordreCsv,
                c.status,
            ];
            return fields.some((f) => f != null && String(f).toLowerCase().includes(term));
        });
    }, [props.patrimoine, searchTerm]);

    if (props.loading || !props.patrimoine) {
        return <Loader/>;
    }

    return (
        <>
            <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} spacing={2}>
                <PageTitle title={`Patrimoine ${props.patrimoine?.libelle}`} icon={<FolderDelete/>}/>
                {(isAdmin() || isExploitant()) &&
                    <ButtonCreate
                        route={Constants.APP_ROUTE_CONTENEUR_NEW.replace(":patrimoineId", patrimoineId)}
                        label="Ajouter un conteneur"
                    />
                }
            </Stack>
            <Grid container justifyContent={"center"} spacing={2} className={"mb-2"}>
                <Grid>
                    <Button
                        variant={getMode() === Constants.MODE_LIST ? "contained" : "outlined"}
                        color="primary"
                        startIcon={<FormatListNumberedIcon/>}
                        onClick={() => handleModeChange(Constants.MODE_LIST)}
                    >
                        Affichage liste
                    </Button>
                </Grid>
                <Grid>
                    <Button
                        variant={getMode() === Constants.MODE_MAP ? "contained" : "outlined"}
                        color="primary"
                        startIcon={<MapIcon/>}
                        onClick={() => handleModeChange(Constants.MODE_MAP)}
                    >
                        Affichage carte
                    </Button>
                </Grid>
            </Grid>
            <Grid container justifyContent={"center"} className={"mb-2"} spacing={1} alignItems={"center"}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="Rechercher (nomenclature, adresse, commune, type...)"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon/>
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label="effacer la recherche"
                                        onClick={() => setSearchTerm("")}
                                    >
                                        <ClearIcon fontSize="small"/>
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                {mode === Constants.MODE_MAP && searchTerm.trim() && (
                    <Grid>
                        <Chip
                            size="small"
                            color={filteredConteneurs.length > 0 ? "primary" : "default"}
                            label={`${filteredConteneurs.length} résultat${filteredConteneurs.length > 1 ? 's' : ''}`}
                        />
                    </Grid>
                )}
            </Grid>
            <Grid container>
                {mode === Constants.MODE_MAP &&
                    <>
                        <Grid size={{ xs: 12 }}>
                            <PatrimoineMap conteneurs={props.patrimoine?.conteneurs}
                                           patrimoine={props.patrimoine}
                                           onConteneurClick={(c) => setCurrentConteneurs(c)}
                                           onConteneurUpdate={onConteneurUpdate}
                                           searchTerm={searchTerm}
                            />
                        </Grid>
                    </>
                }
                {mode === Constants.MODE_LIST &&
                    <>
                        <Grid size={{ xs: 12 }}>
                            {(Constants.FEATURE_CONTENEUR_CONSIGNES && !props.patrimoine?.isUnipatrimoine)
                                ? <PatrimoineDetailListV2 conteneurs={filteredConteneurs}
                                                          patrimoine={props.patrimoine}
                                                          patrimoineId={patrimoineId}
                                                          onRefresh={() => dispatch(patrimoinesActions.fetchPatrimoine(patrimoineId))}/>
                                : <PatrimoineDetailList conteneurs={filteredConteneurs}
                                                        patrimoine={props.patrimoine}
                                                        patrimoineId={patrimoineId}/>}
                        </Grid>
                    </>
                }
            </Grid>

        </>
    )
}

const mapStateToProps = function (state) {
    return {
        patrimoine: state.patrimoines.patrimoine,
        loading: state.patrimoines.loading,
        error: state.patrimoines.error
    }
}

export default connect(mapStateToProps)(PatrimoineDetailPage);
